import type { UserRole } from '@coreltb/shared/constants';

/**
 * „Rozbiórka starego wyglądu” (Dawid 2026-09-30): przy pierwszym otwarciu nowej
 * wersji każdy widzi swój stary CRM i rozwala go młotkiem. Raz na osobę na
 * urządzeniu; po REVEAL_UNTIL już nikomu (nowi ludzie starego wyglądu nie znają).
 * `?stary-wyglad` (lub `?stary-wyglad=wiktor`) odpala ponownie — do pokazów.
 */
export const REVEAL_PEOPLE = ['dawid', 'tomek', 'wiktor', 'blazej', 'jakub', 'julia'] as const;
export type RevealPerson = (typeof REVEAL_PEOPLE)[number];

export const REVEAL_STORAGE_KEY = 'coreltb.newDesignReveal.v1';
export const REVEAL_PARAM = 'stary-wyglad';
const REVEAL_UNTIL = new Date('2026-12-31T23:59:59+01:00').getTime();

const BY_EMAIL: Readonly<Record<string, RevealPerson>> = {
  'dawidfc@gmail.com': 'dawid',
  'tomasz.ledzki@coreltb.pl': 'tomek',
  'biuro@coreltb.pl': 'julia',
};

const BY_NAME: Readonly<Record<string, RevealPerson>> = {
  'dawid rduch': 'dawid',
  'tomasz ledzki': 'tomek',
  'wiktor jablonka': 'wiktor',
  'blazej siemionek': 'blazej',
  'jakub kotrys': 'jakub',
  'julia kubala': 'julia',
};

const BY_ROLE: Partial<Record<UserRole, RevealPerson>> = {
  developer: 'dawid',
  admin: 'tomek',
  dyrektor_sprzedazy: 'tomek',
  handlowiec: 'wiktor',
  marketing: 'julia',
};

interface RevealUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
}

function normalizeName(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/ł/g, 'l')
    .replace(/\s+/g, ' ')
    .trim();
}

function personFor(user: RevealUser): RevealPerson | null {
  return BY_EMAIL[user.email.toLowerCase()] ?? BY_NAME[normalizeName(user.name)] ?? null;
}

function isPerson(value: string): value is RevealPerson {
  return (REVEAL_PEOPLE as readonly string[]).includes(value);
}

function readDone(): string[] {
  try {
    const raw = window.localStorage.getItem(REVEAL_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

/** Czyj stary CRM pokazać — albo null, gdy rozbiórki nie będzie. */
export function revealFor(user: RevealUser, search: string): RevealPerson | null {
  const forced = new URLSearchParams(search).get(REVEAL_PARAM);
  if (forced !== null) {
    if (isPerson(forced)) return forced;
    return personFor(user) ?? BY_ROLE[user.role] ?? 'dawid';
  }
  if (Date.now() > REVEAL_UNTIL || readDone().includes(user.id)) return null;
  return personFor(user);
}

export function markRevealed(userId: string): void {
  try {
    const done = readDone();
    if (!done.includes(userId)) done.push(userId);
    window.localStorage.setItem(REVEAL_STORAGE_KEY, JSON.stringify(done));
  } catch {
    // localStorage zablokowany — rozbiórka wróci przy następnym otwarciu, trudno.
  }
}
